/**
 * Finding Befund references inside free text.
 *
 * A Tagebuch day, a Befund description and a Gesamtbeschreibung are all written
 * in prose — "Bef. 7000 geschnitten, Befund 7012 gezeichnet". This splits such a
 * text into plain runs and reference runs so a renderer can set the references
 * apart: the Tagebuch sheet rings them, the editor chips them.
 *
 * Pure string work, no layout and no data: what a reference LOOKS like is the
 * renderer's business, and WHICH record it is belongs to whoever holds them.
 */

/** One run of prose, or a Befund the prose names. */
export type ProseSegment =
    | { kind: 'text'; text: string }
    | ({
          kind: 'ref';
          text: string;
          /**
           * Where the Befundnummer starts inside `text`. The editor sets only
           * the number bold, and marks the range in the document rather than
           * re-finding it in a string.
           */
          numberStart: number;
      } & FindingRef);

/** What a reference in the text says. Only `number` is ever certain. */
export interface FindingRef {
    /** The Befundnummer, e.g. "7000" — the part that is really the Befund. */
    number: string;
    /** Fläche, when the text placed the Befund on one ("Bef. 7000 Fl. 1 Pl. 2"). */
    area?: string;
    /** Planumnummer — this OR `profileNumber`, never both. */
    planumNumber?: string;
    /** Profilnummer — this OR `planumNumber`, never both. */
    profileNumber?: string;
    /**
     * Set when the text names a Gesamtbeschreibung ("Gesb. 1") rather than a
     * Befund; `number` is then the Gesamtbeschreibungsnummer.
     */
    overallDescription?: true;
}

/**
 * The gaps are spelled with escapes, not typed: a no-break space (the field
 * tool's exports do contain them) is invisible in a pattern, and an invisible
 * character is one nobody can review.
 */
const GAP = '[\\u0020\\u00a0]*';
/** A number as these fields are numbered: digits, optionally suffixed ("7000a"). */
const NUMBER = '(\\d+\\p{Ll}?)';
/**
 * The Befund itself, in the spellings the field tool and the recorders actually
 * use: "Befund 7000", "Bef. 7000", "Bef 7000", "B. 7000", with or without the
 * space between prefix and number.
 *
 * Two guards keep it from marking things that are not Befunde:
 *
 * - The lookbehind drops "z. B. 3" — German's "zum Beispiel" abbreviates to the
 *   same "B." and is regularly followed by a count.
 * - The prefix must start a word, so "Verbund 7000" is not a Befund.
 *
 * A number WITHOUT a prefix is deliberately left alone, including the second one
 * in "Bef. 7000, 7012": marking it would mean marking every number after a
 * comma, and "Bef. 7000, 5 m nördlich" would mark the 5.
 */
const FINDING = `(?<!\\p{L})(?<!z\\.\\s?)(?<!z\\s)(?:Befunde?|Bef\\.?|B\\.)${GAP}${NUMBER}`;
/**
 * What may follow to name one DESCRIPTION of that Befund rather than the whole
 * of it: an optional Fläche, then a Planum or a Profil. Written with or without
 * the comma the app's own labels use, and long or abbreviated.
 *
 * The separator admits a comma and spaces but NOT a newline: across a line break
 * the next sentence's "Planum 1" is a new statement, not this Befund's Planum.
 *
 * Each part is optional as a WHOLE, so "Bef. 7000, Fläche wurde geputzt" matches
 * the Befund alone — the Fläche group needs a number after the word and
 * backtracks away when there is none.
 */
const SEPARATOR = `,?${GAP}`;
const AREA = `(?:${SEPARATOR}Fl(?:\\.|äche)?${GAP}${NUMBER})?`;
const PLANUM = `Pl(?:\\.|anum)?${GAP}${NUMBER}`;
const PROFILE = `Pr(?:\\.|ofil)?${GAP}${NUMBER}`;
/**
 * A Planum OR a Profil, never both — there is no combined description, so a
 * reference cannot name one. In "Bef. 10631, Pl. 2, Pr. 10614" the reference
 * therefore ENDS after the Planum and the rest is prose: marking the whole of
 * it would ring a record that does not exist and cannot be opened.
 */
const DESCRIPTION = `(?:${SEPARATOR}(?:${PLANUM}|${PROFILE}))?`;

/**
 * A Gesamtbeschreibung: "Gesamtbeschreibung 1", "Gesb. 1", "Gesb 1". Its own
 * alternative after the Befund one, so its number is a group of its own.
 */
const OVERALL_DESCRIPTION = `(?<!\\p{L})(?:Gesamtbeschreibung|Gesb\\.?)${GAP}${NUMBER}`;

/** Befund only — what the Tagebuch sheet rings. Group 1 is the number. */
const REFERENCE = new RegExp(FINDING, 'giu');
/**
 * With its description, or a Gesamtbeschreibung. Groups: 1 number, 2 Fläche,
 * 3 Planum, 4 Profil, 5 Gesamtbeschreibungsnummer.
 */
const REFERENCE_WITH_DESCRIPTION = new RegExp(
    `${FINDING}${AREA}${DESCRIPTION}|${OVERALL_DESCRIPTION}`,
    'giu'
);

export interface SplitOptions {
    /**
     * Also read the Fläche/Planum/Profil that may follow, so a reference can
     * name one description rather than the whole Befund, and the
     * Gesamtbeschreibungen. OFF by default: the Tagebuch PDF rings the
     * Befundnummer alone and must keep doing so.
     */
    withDescription?: boolean;
}

/** Split a text into its prose and the Befunde it names, in order. */
export function splitFindingRefs(
    text: string,
    { withDescription = false }: SplitOptions = {}
): ProseSegment[] {
    if (!text) {
        return [];
    }
    const pattern = withDescription ? REFERENCE_WITH_DESCRIPTION : REFERENCE;
    const segments: ProseSegment[] = [];
    let cursor = 0;
    // `matchAll` rather than an `exec` loop: the patterns are module-level and
    // therefore shared, and a stateful `lastIndex` would leak between calls.
    for (const match of text.matchAll(pattern)) {
        const start = match.index ?? 0;
        if (start > cursor) {
            segments.push({ kind: 'text', text: text.slice(cursor, start) });
        }
        const number = match[1] ?? match[5];
        segments.push({
            kind: 'ref',
            text: match[0],
            number,
            /**
             * No prefix this pattern accepts contains a digit, so the first
             * occurrence of the number IS the number.
             */
            numberStart: match[0].indexOf(number),
            // Left OUT rather than set to undefined, so a whole-Befund
            // reference still compares equal to a plain `{ number }`.
            ...(match[2] ? { area: match[2] } : {}),
            ...(match[3] ? { planumNumber: match[3] } : {}),
            ...(match[4] ? { profileNumber: match[4] } : {}),
            ...(match[5] ? { overallDescription: true as const } : {})
        });
        cursor = start + match[0].length;
    }
    if (cursor < text.length) {
        segments.push({ kind: 'text', text: text.slice(cursor) });
    }
    return segments;
}

/** Whether a text names any Befund at all — lets a caller skip the rich path. */
export function hasFindingRef(text: string): boolean {
    return splitFindingRefs(text).some((segment) => segment.kind === 'ref');
}

/** A word, a ringed Befund, or a break the recorder typed. */
export type ProseToken =
    | { kind: 'word'; text: string; space: boolean }
    | {
          kind: 'ref';
          text: string;
          space: boolean;
          /** The Befundnummer, and where it sits — only IT is set bold. */
          number: string;
          numberStart: number;
      }
    | { kind: 'break'; paragraph: boolean };

/**
 * A text as the pieces a ringed paragraph is laid out from: one item per WORD,
 * because a ring is a box and a box cannot flow inside a run of text (see
 * RefProse), so the paragraph has to break its own lines.
 *
 * Whitespace becomes a FLAG rather than an item: a space cannot simply be put
 * after every piece, because a reference is regularly followed by punctuation —
 * "Bef. 7000, gezeichnet" would otherwise set as "Bef. 7000 , gezeichnet".
 * Whitespace holding a newline becomes a break instead, so a recorder's own line
 * and paragraph breaks survive the relayout.
 */
export function tokenizeProse(text: string): ProseToken[] {
    const tokens: ProseToken[] = [];
    const spaceAfterLast = () => {
        const last = tokens[tokens.length - 1];
        if (last && last.kind !== 'break') {
            last.space = true;
        }
    };
    for (const segment of splitFindingRefs(text)) {
        if (segment.kind === 'ref') {
            tokens.push({
                kind: 'ref',
                text: segment.text,
                space: false,
                number: segment.number,
                numberStart: segment.numberStart
            });
            continue;
        }
        // Split KEEPING the separators, so each run of whitespace can be looked at.
        for (const part of segment.text.split(/(\s+)/)) {
            if (part === '') {
                continue;
            }
            if (/^\s+$/.test(part)) {
                if (part.includes('\n')) {
                    tokens.push({
                        kind: 'break',
                        paragraph: /\n[^\S\n]*\n/.test(part)
                    });
                } else {
                    spaceAfterLast();
                }
                continue;
            }
            tokens.push({ kind: 'word', text: part, space: false });
        }
    }
    return tokens;
}
